/* Product HTML stays readable without JavaScript. Move original nodes; never
   re-create merchant HTML or infer missing measurements / fabric properties. */
(() => {
  if (customElements.get('pancha-product-details')) return;

  const meaningful = (node) => Boolean(node.textContent.trim() || node.querySelector('img, video, iframe, svg, audio, input'));
  const text = (node) => node.textContent.trim().replace(/[\s：:()（）]/g, '');
  const normalize = (value) => value.trim().replace(/[\s：:()（）]/g, '').toLowerCase();
  const fitGroups = [
    { key: 'thickness', names: ['厚さ', 'Thickness'], choices: [['薄い', 'Thin'], ['普通', 'Normal', 'Regular', 'Medium', 'Standard'], ['やや厚い', 'Slightly thick', 'Slightly thicker'], ['厚い', 'Thick']] },
    { key: 'softness', names: ['柔らかさ', 'Softness'], choices: [['柔らかい', 'Soft'], ['普通', 'Normal', 'Regular', 'Medium', 'Standard'], ['やや硬い', 'Slightly firm', 'Slightly stiff', 'Slightly hard'], ['硬い', 'Firm', 'Stiff', 'Hard']] },
    { key: 'stretch', names: ['弾力性', 'Elasticity', 'Stretch'], choices: [['なし', 'None', 'No stretch'], ['ややあり', 'Slight', 'Slight stretch', 'Some stretch'], ['あり', 'Stretchy', 'Stretch', 'Yes', 'Moderate stretch'], ['高弾力', 'High stretch', 'Highly elastic', 'High elasticity']] },
    { key: 'fit', names: ['スタイル', 'Style', 'Fit'], choices: [['きつめ', 'Tight', 'Snug'], ['ぴったり', 'Fitted', 'Close-fitting'], ['適度', 'Regular', 'Moderate', 'Regular fit'], ['ゆったり', 'Loose', 'Relaxed']] }
  ];
  const groupFor = (label) => fitGroups.find((group) => group.names.some((name) => normalize(name) === normalize(label)));
  const sizeNames = ['サイズ', '着丈', '衣丈', '身丈', '肩幅', '胸囲', 'バスト', 'ウエスト', '袖丈', 'ヒップ', '股下', 'Size', 'Length', 'Shoulder', 'Chest', 'Bust', 'Waist', 'Sleeve', 'Hip', 'Inseam'];
  const isUnit = (node) => /^(?:単位|unit|units)?(?:cm|mm)$/i.test(text(node));

  class PanchaProductDetails extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      const copy = this.querySelector('[data-pancha-product-i18n]');
      this.copy = copy ? JSON.parse(copy.textContent) : {
        size: 'サイズ', fit: '生地・着用感', currentValue: '（この商品の値）',
        tableRegion: '商品情報の表（横にスクロールできます）',
        scales: Object.fromEntries(fitGroups.map((group) => [group.key, group.choices.map((choices) => choices[0]).join('|')]))
      };
      this.enhanceDescription();
      this.tabs = [...this.querySelectorAll('[data-pancha-tab]')];
      this.panels = [...this.querySelectorAll('[data-pancha-panel]')];
      this.querySelector('.pancha-product-details__tabs').setAttribute('role', 'tablist');
      this.tabs.forEach((tab, index) => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', this.panels[index].id);
        this.panels[index].setAttribute('role', 'tabpanel');
        this.panels[index].tabIndex = 0;
        tab.addEventListener('click', (event) => { event.preventDefault(); this.activate(index); });
        tab.addEventListener('keydown', (event) => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index + this.tabs.length - 1) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (event.key === ' ') next = index;
          if (next === undefined) return;
          event.preventDefault();
          this.activate(next);
          this.tabs[next].focus();
        });
      });
      const linked = this.panels.findIndex((panel) => '#' + panel.id === window.location.hash);
      this.activate(linked < 0 ? 0 : linked);
      this.classList.add('is-enhanced');
    }

    activate(index) {
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
        this.panels[i].hidden = i !== index;
      });
    }

    enhanceDescription() {
      const raw = this.querySelector('[data-pancha-description]');
      if (!raw) return;
      const productCopy = this.querySelector('template[data-pancha-product-copy]');
      if (productCopy?.content.querySelector('.product-intro, .product-detail')) {
        raw.replaceChildren(...productCopy.content.childNodes);
      }
      const intro = this.closest('.pancha-product')?.querySelector('[data-pancha-product-intro]');
      if (intro) {
        const introductions = [...raw.querySelectorAll('.product-intro')]
          .filter((node) => !node.parentElement.closest('.product-intro'));
        intro.replaceChildren(...introductions);
        intro.hidden = !meaningful(intro);
      }
      const parts = {};
      // Explicit sections take precedence. Unknown content remains in the raw
      // description, including additional tables and product-specific notices.
      ['intro', 'fit', 'size', 'specs'].forEach((key) => {
        const candidates = [...raw.querySelectorAll('.pancha-description__' + key)]
          .filter((node) => !node.parentElement.closest('[class*="pancha-description__"]'));
        if (!candidates.length) return;
        const part = document.createElement('div');
        part.className = 'pancha-detail-part pancha-detail-part--' + key;
        candidates.forEach((node) => part.append(node));
        if (meaningful(part)) parts[key] = part;
      });

      // Existing catalog HTML uses .sizes for clothing, shoes, bags and sets.
      // Keep its adjacent unit with the table, including multi-table products.
      const hasExplicitSize = Boolean(parts.size);
      [...raw.querySelectorAll('table')].forEach((table) => {
        if (table.parentElement.closest('table')) return;
        const rows = [...table.rows];
        const labels = rows.map((row) => row.cells[0] ? text(row.cells[0]) : '');
        const headers = rows[0] ? [...rows[0].cells].map(text) : [];
        let key;
        if (!parts.fit && ((table.matches('.sizeinfo') && rows.length === 4 && rows.every((row) => row.cells.length === 5)) || (labels.length === 4 && new Set(labels.map((label) => groupFor(label)?.key).filter(Boolean)).size === 4))) key = 'fit';
        else if (!hasExplicitSize && (table.matches('.sizes') || (headers.some((name) => /^(?:サイズ|size)(?:cm|mm)?$/i.test(name)) && headers.filter((name) => sizeNames.some((known) => normalize(name).includes(normalize(known)))).length >= 3))) key = 'size';
        if (!key) return;
        const part = parts[key] || document.createElement('div');
        part.className = 'pancha-detail-part pancha-detail-part--' + key;
        const previous = table.previousElementSibling;
        const next = table.nextElementSibling;
        if (previous && /^H[2-6]$/.test(previous.tagName) && /サイズ|着用感|生地|size|fabric|fit/i.test(previous.textContent)) part.append(previous);
        if (!part.querySelector('h2, h3, h4, h5, h6')) {
          const heading = document.createElement('h3');
          heading.className = 'pancha-detail-heading';
          heading.textContent = this.copy[key];
          part.append(heading);
        }
        part.append(table);
        if (key === 'size' && next && isUnit(next)) {
          next.classList.add('pancha-size-unit');
          part.append(next);
        }
        parts[key] = part;
      });

      if (!Object.keys(parts).length) {
        this.wrapTables(raw);
        return;
      }
      if (parts.intro) raw.prepend(parts.intro);
      const tags = [...raw.querySelectorAll('#item_tags')];
      tags.forEach((list) => { list.classList.add('pancha-product-tags'); raw.after(list); });
      const top = document.createElement('div');
      top.className = 'pancha-description-row pancha-description-row--top';
      raw.before(top);
      top.append(raw);
      if (parts.fit) {
        this.enhanceFit(parts.fit);
        top.append(parts.fit);
        top.classList.add('has-fit');
      }
      if (!meaningful(raw)) raw.hidden = true;
      else top.classList.add('has-intro');
      if (parts.size || parts.specs) {
        const bottom = document.createElement('div');
        bottom.className = 'pancha-description-row pancha-description-row--bottom';
        top.after(bottom);
        if (parts.size) bottom.append(parts.size);
        if (parts.specs) bottom.append(parts.specs);
        if (parts.size && parts.specs) bottom.classList.add('has-both');
      }
      if (!meaningful(top)) top.hidden = true;
      this.wrapTables(this.querySelector('[data-pancha-panel]'));
    }

    enhanceFit(part) {
      const source = part.querySelector('table, dl');
      if (!source || source.caption) return;
      let entries;
      const rows = source.matches('table') ? [...source.rows] : [];
      if (rows.length === 4 && rows.every((row) => row.cells.length === 5 && (source.matches('.sizeinfo') || groupFor(text(row.cells[0]))))) {
        entries = rows.map((row) => {
          const choices = [...row.cells].slice(1);
          const selected = choices.filter((cell) => {
            if (cell.matches('[data-selected="true"], .is-selected, [aria-current="true"]')) return true;
            if (!source.matches('.sizeinfo')) return false;
            const background = cell.style.backgroundColor.replace(/\s/g, '').toLowerCase();
            const underline = cell.querySelector('u');
            return background === 'rgb(243,243,243)' || background === '#f3f3f3' || (underline && text(underline) === text(cell));
          });
          return { label: row.cells[0], choices, selected };
        });
      } else {
        let pairs;
        if (source.matches('table') && rows.every((row) => row.cells.length === 2)) {
          pairs = rows.map((row) => [...row.cells]);
        } else if (source.matches('dl')) {
          const groups = [...source.children];
          const whitespaceOnly = (node) => [...node.childNodes].every((child) => child.nodeType !== Node.TEXT_NODE || !child.textContent.trim());
          if (!whitespaceOnly(source) || !groups.every((group) => group.matches('div') && whitespaceOnly(group) && group.children.length === 2 && group.children[0].matches('dt') && group.children[1].matches('dd'))) return;
          pairs = groups.map((group) => [...group.children]);
        } else return;
        entries = pairs.map(([label, value]) => {
          const group = groupFor(text(label));
          if (!group) return null;
          const selectedIndex = group.choices.findIndex((choices) => choices.some((choice) => normalize(choice) === normalize(text(value))));
          if (selectedIndex < 0) return null;
          return { label, choices: this.copy.scales[group.key].split('|').map((choice, index) => index === selectedIndex ? value : choice), selected: [value] };
        });
        if (!entries.length || entries.some((entry) => !entry)) return;
      }
      if (entries.some((entry) => entry.selected.length > 1) || new Set(entries.map((entry) => text(entry.label))).size !== entries.length) return;
      const list = document.createElement('dl');
      list.className = 'pancha-fit';
      entries.forEach((entry) => {
        const line = document.createElement('div');
        line.className = 'pancha-fit__row';
        const label = document.createElement('dt');
        label.append(...entry.label.childNodes);
        const values = document.createElement('dd');
        const scale = document.createElement('ol');
        scale.className = 'pancha-fit__scale';
        entry.choices.forEach((choice) => {
          const value = document.createElement('li');
          if (typeof choice === 'string') value.textContent = choice;
          else value.append(...choice.childNodes);
          if (choice === entry.selected[0]) {
            value.classList.add('is-selected');
            const current = document.createElement('span');
            current.className = 'visually-hidden';
            current.textContent = this.copy.currentValue;
            value.append(current);
          }
          scale.append(value);
        });
        values.append(scale);
        line.append(label, values);
        list.append(line);
      });
      source.replaceWith(list);
    }

    wrapTables(root) {
      root.querySelectorAll('table').forEach((table) => {
        if (table.parentElement.classList.contains('pancha-table-scroll')) return;
        const unit = table.nextElementSibling;
        if (table.closest('.pancha-detail-part--size, .pancha-description__size') && unit && isUnit(unit)) unit.classList.add('pancha-size-unit');
        const scroll = document.createElement('div');
        scroll.className = 'pancha-table-scroll';
        scroll.tabIndex = 0;
        scroll.setAttribute('role', 'region');
        scroll.setAttribute('aria-label', table.caption?.textContent.trim() || this.copy.tableRegion);
        table.before(scroll);
        scroll.append(table);
      });
    }
  }
  customElements.define('pancha-product-details', PanchaProductDetails);
})();
